// src/lib/apiFetch.ts

type ApiMethod = "GET" | "POST" | "PUT" | "DELETE" | "PATCH";

interface ApiFetchOptions extends Omit<RequestInit, "method" | "body"> {
  method?: ApiMethod;
  body?: Record<string, any>; // JSON body
}

// 👇 Add this helper at the top
function getCookie(name: string): string | null {
  const match = document.cookie.match(new RegExp("(^| )" + name + "=([^;]+)"));
  return match ? match[2] : null;
}


export async function apiFetch<T = any>(
  url: string,
  options: ApiFetchOptions = {}
): Promise<T> {
  const token = localStorage.getItem("access_token");

  const headers: HeadersInit = {
    Accept: "application/json",
    "Content-Type": "application/json",
    ...(options.headers || {}),
  };

  if (token && !headers["Authorization"]) {
    headers["Authorization"] = `Bearer ${token}`;
  }
  
  const xsrfToken = getCookie("XSRF-TOKEN");
  if (xsrfToken) {
      headers["X-XSRF-TOKEN"] = decodeURIComponent(xsrfToken);
  }

  const fetchOptions: RequestInit = {
    ...options,
    method: options.method || "GET",
    credentials: "include",
    headers,
    body: options.body ? JSON.stringify(options.body) : undefined,
  };

  const res = await fetch(url, fetchOptions);

  const contentType = res.headers.get("Content-Type") || "";

  if (res.status === 401) {
    if (!options.suppressRedirect) {
      localStorage.removeItem("access_token");
      localStorage.removeItem("user");
      localStorage.removeItem("isAuthenticated");
      window.location.href = "/login?expired=true";
    }

    throw new Error("Unauthorized");
  }

  if (!res.ok) {
    let message = `Error ${res.status}`;
    let data = null;

    const contentType = res.headers.get("Content-Type") || "";
    if (contentType.includes("application/json")) {
      data = await res.json();
      message = data.error || data.message || message; // <-- prioritize server-side message
    } else {
      const text = await res.text();
      message = text || message;
    }

    const error = new Error(message);
    (error as any).details = data;
    throw error;
  }

  if (contentType.includes("application/json")) {
    return res.json() as Promise<T>;
  }

  // If not JSON, return plain text
  return (await res.text()) as unknown as T;
}
